/**
 * AI:
 * Status: "confirmed"
 * Type: Page
 * SubType: CrudIntegration
 * Reason: Mounts an autosaving mvs-crud-object-generic for xx.XxAutosaveFocusMock to A/B the
 *         inPlaceUpdate flag — the saved row is merged into the live form group instead of rebuilding
 *         it, so focus and caret survive the write and a dependsOn field can appear while typing.
 *         A second section mounts the same component against a real backend entity.
 */
import {Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit} from '@angular/core';
import {DtoDetail} from '../../../../core/shared/dto/dto.detail';
import {
    MvsFormStateDto
} from '../../../../core/shared/crud/components/mvs-crud-object-form/dto/mvs-form-state.dto';
import {
    MvsFormControlOverwrite
} from '../../../../core/shared/logic/form/logic/mvs-form-control-overwrite';
import {
    MvsFormFieldAccessEnum
} from '../../../../core/shared/logic/form/logic/mvs-form-field-access.enum';
import {
    ObjectChangeInformation,
    ObjectChangeInformationActionEnum
} from '../../../../core/shared/logic/object-change-information';
// Imported for ONE reason: the latency selector writes the static the mock reads. No instance of the
// service is created here — the form resolves it by alias through MvsCoreService like any other.
import {XxAutosaveFocusMockService} from '../../service/api/xx-autosave-focus-mock.service';
// @ticket TM-1000002003: used ONLY to ask whether a typed object type resolves at all, before the
// backend section mounts a form for it. Already in the graph of every consumer of the CRUD
// components, and this page is in no karma entry, so the import adds nothing to any test bundle.
import {MvsCoreService} from '../../../../core/shared/service/mvs-core.service';

/** One entry of the event log — newest first. */
interface XxAutosaveFocusLogEntry {
    /** Running number, so two events in the same millisecond stay distinguishable. */
    seq: number;
    /** hh:mm:ss.SSS of the moment the event arrived. */
    time: string;
    /** Short name of the source, e.g. "onChangedObject". */
    name: string;
    /** German one-line summary. */
    detail: string;
    /** Drives the colour of the row marker. */
    severity: 'info' | 'success' | 'warn' | 'danger';
}

/** The latencies the mock can be switched to. */
const LATENCY_OPTIONS = [
    {label: '0 ms — sofort', value: 0},
    {label: '800 ms — realistisch', value: 800},
    {label: '2500 ms — langsam (Tippen währenddessen)', value: 2500}
];

/**
 * @ticket TM-1000002003
 * Testseite: Autosave OHNE Fokusverlust — route `xx/test/autosave-focus`.
 *
 * <h3>The question this page answers</h3>
 * Can a metadata form save itself while the user is typing, and let a field whose visibility depends
 * on another field appear and disappear, WITHOUT the input element under the caret ever being
 * destroyed? The page A/Bs exactly that: one toggle, `inPlaceUpdate`, and the same form beside itself
 * in both states.
 *
 * <h3>Two defects are reproducible here, both with the toggle OFF</h3>
 * <ol>
 *   <li><b>Fokusverlust nach dem Speichern.</b> The saved row comes back, the form group is rebuilt,
 *       every input element is recreated — the focused field becomes „— (Body)“ in the diagnostics
 *       panel mid-word.</li>
 *   <li><b>Verlorene Zeichen.</b> Keystrokes made while the write is in flight arm the debounce; the
 *       debounce fires into a busy form and is dropped, and nothing re-arms it. Set the latency to
 *       2500 ms and type continuously to see it.</li>
 * </ol>
 *
 * <h3>Why it mounts `mvs-crud-object-generic` and NOT the `mvs-crud-object` wrapper</h3>
 * The wrapper's `ngOnChanges` calls `refreshComponent(false)` for ANY input change, which clears its
 * view container and creates the inner component from scratch. A new `formControlOverwrite` instance
 * would therefore destroy the whole form before the in-place path is ever reached — the dependent
 * visibility case would be neither reproducible nor fixable through it. cw mounts the generic
 * component directly for the same reason.
 *
 * <h3>No focus handling anywhere in this file</h3>
 * The diagnostics panel READS `document.activeElement` to report where the focus is. It never calls
 * `focus()` and never touches a selection range. That is the point: if the caret survives, it is
 * because nothing was destroyed, not because something put it back.
 *
 * <h3>Two sections</h3>
 * The upper one runs against an in-memory mock CRUD service (`xx.XxAutosaveFocusMock`) — no backend is
 * involved, so it can be opened anywhere and its latency is a dropdown. The lower one mounts the very
 * same component against a REAL entity of the running backend (`cr.Customer` by default), because a
 * mock can only ever prove something about the framework, not about a form anybody uses.
 */
@Component({
    selector: 'mvs-xx-autosave-focus-test',
    templateUrl: './xx-autosave-focus-test.page.html',
    styleUrls: ['./xx-autosave-focus-test.page.scss'],
    standalone: false
})
export class XxAutosaveFocusTestPage implements OnInit, OnDestroy {

    /** The entity the mock is registered under in `xx.entity-provider.ts`. */
    readonly objectType: string = 'xx.XxAutosaveFocusMock';

    /** The seeded row. */
    readonly objectId: number = 1;

    /** The still-stand before autosave writes. */
    readonly autoSaveDebounceMs: number = 400;

    readonly latencyOptions = LATENCY_OPTIONS;

    /** THE toggle — the whole point of the page. */
    inPlace: boolean = true;

    /** Simulated round-trip time of the mock. */
    latencyMs: number = 800;

    /**
     * Bumped on „Formular neu aufbauen“ — used as the track key of a single-element `@for`, so the
     * whole component is destroyed and recreated and every collected state starts clean again.
     */
    formInstance: number = 1;

    /** Latest `(onFormChange)` payload — what is on screen right now. */
    formValue: DtoDetail = null;

    /** Latest `(onFormStateChange)` snapshot. */
    state: MvsFormStateDto = null;

    /** Latest `(onFormDirty)` value, shown next to the snapshot so drift would be visible. */
    dirtyOutput: boolean = null;

    /** How many saves have come back so far. */
    saveCount: number = 0;

    /**
     * @ticket TM-1000002003
     * What the last server template refresh cost: „in place" when the live form group survived it,
     * „Neuaufbau" when it was replaced. Derived from the group INSTANCE, which is the only honest
     * witness — a value check would pass either way.
     */
    templateRefreshMode: string = '—';

    // ── live focus diagnostics (READ ONLY — see the class TSDoc) ────────────────

    /** German label of the field the caret is in, or „— (Body)“. */
    focusedFieldLabel: string = '— (Body)';

    /** Caret position inside that field. */
    caretStart: number = null;
    caretEnd: number = null;

    /** The value of the focused input, so a lost character is visible without reading the form. */
    focusedFieldValue: string = null;

    eventLog: XxAutosaveFocusLogEntry[] = [];

    private seq: number = 0;

    /** The `selectionchange` listener, kept so ngOnDestroy can take it off the document again. */
    private selectionListener: (() => void) | null = null;

    constructor(private elementRef: ElementRef, private zone: NgZone,
                private coreService: MvsCoreService) {
    }

    /**
     * @ticket TM-1000002003
     * The caret readout is driven by EVENTS, and the events are bound OUTSIDE the Angular zone.
     *
     * <h3>What was here before, and why it had to go</h3>
     * A `setInterval(…, 100)` created inside the zone. Every tick ran a full application-wide change
     * detection pass — ten per second, for as long as the page was open, on a shell this size. Not a
     * loop, a treadmill: it never ends and it never looks like a defect in any call stack. It cost
     * real renderer CPU and made the page the slowest thing on the machine it was supposed to be
     * measuring.
     *
     * <p>`selectionchange` is the event the caret actually has — it fires on every cursor move inside
     * the focused input, which is exactly and only when this panel's numbers can change. Bound outside
     * the zone it costs nothing until it fires, and it re-enters the zone ONLY when a value really
     * moved, so a cursor sitting still triggers no render at all.</p>
     */
    ngOnInit(): void {

        XxAutosaveFocusMockService.latencyMs = this.latencyMs;

        this.zone.runOutsideAngular(() => {
            this.selectionListener = () => this.refreshFocusDiagnostics();
            document.addEventListener('selectionchange', this.selectionListener);
        });

        this.log('Seite', `Bereit. In-Place-Update ist ${this.inPlace ? 'AN' : 'AUS'}.`, 'info');
    }

    ngOnDestroy(): void {
        if (this.selectionListener) {
            document.removeEventListener('selectionchange', this.selectionListener);
            this.selectionListener = null;
        }
        // @ticket TM-1000002003: a pending „no answer“ timer would otherwise write to a destroyed page
        this.clearRealAnswerTimer();
    }

    // ----------------------------------------------------------------------------------- //
    // DEPENDENT VISIBILITY — exactly the cw mechanism
    // ----------------------------------------------------------------------------------- //

    /** The overwrite instance currently handed to the form. */
    private cachedOverwrite: MvsFormControlOverwrite = null;

    /** The signature the cached instance was built for. */
    private cachedSignature: string = null;

    /**
     * @ticket TM-1000002003
     * „gen_details“ ist nur sichtbar, wenn „Details vorhanden?“ den Wert „ja“ trägt.
     *
     * <h3>Why the INSTANCE is memoised and not just the content</h3>
     * The overwrite is an `@Input`, so Angular compares it by identity. Handing out a new instance on
     * every keystroke would make the form react to every keystroke — and with `inPlaceUpdate` OFF that
     * means a full rebuild per character, i.e. the caret lost after the first letter. A new instance is
     * therefore produced ONLY when the hidden list really changed. This is the same `cacheOverwrite`
     * technique the cw section renderer uses, for the same reason.
     */
    get overwrite(): MvsFormControlOverwrite {

        const hidden: string[] = this.hiddenFields();
        // The separator cannot occur in a field id (they are DTO attribute names), so the join is
        // unambiguous — no two different lists can produce the same signature.
        const signature: string = hidden.join('|');

        if (this.cachedOverwrite && this.cachedSignature === signature) {
            return this.cachedOverwrite;
        }

        const built = new MvsFormControlOverwrite();
        for (const name of hidden) {
            built.addField(name, MvsFormFieldAccessEnum.HIDDEN);
        }

        this.cachedOverwrite = built;
        this.cachedSignature = signature;

        return built;
    }

    /** The current hidden list — the only business rule on this page. */
    private hiddenFields(): string[] {

        const master: any = this.formValue ? this.formValue['gen_hasDetails'] : null;
        const showDetails: boolean = String(master ?? '').trim().toLowerCase() === 'ja';

        return showDetails ? [] : ['gen_details'];
    }

    /** Whether the dependent field is currently on screen — mirrored in the diagnostics panel. */
    get detailsVisible(): boolean {
        return this.hiddenFields().length === 0;
    }

    // ----------------------------------------------------------------------------------- //
    // FORM OUTPUTS
    // ----------------------------------------------------------------------------------- //

    /**
     * @ticket TM-1000002003
     * Apply a diagnostics update AFTER the running change-detection pass.
     *
     * <h3>Why every output handler below goes through this</h3>
     * The in-place path answers a saved row from inside
     * {@code MvsCrudObjectFormComponent.ngOnChanges}, i.e. while Angular is already checking THIS
     * page's view. Anything the handlers write is therefore written after the page's own bindings
     * were read, and dev mode reports it as
     * {@code NG0100 ExpressionChangedAfterItHasBeenCheckedError} — measured on this page, once per
     * merge. The values were correct; what was wrong was the moment they moved.
     *
     * <p>Deferring by one microtask puts the write after the pass and schedules the next one, so the
     * panel is one microtask behind and consistent. Nothing here is business state — it is a
     * read-out — so the delay costs nothing.</p>
     *
     * <p>The same rule applies to {@code onFormChange} and {@code onFormDirty} on this path: they fire
     * synchronously inside a change-detection pass, so a host that renders what it reports has to do
     * the same thing.</p>
     */
    private afterChangeDetection(apply: () => void): void {
        Promise.resolve().then(apply);
    }

    /**
     * @ticket TM-1000002003
     * The form asked the server what a {@code refreshFormOnChange} value implies and got a new
     * definition back. Compare the group instance across it: same instance = applied in place.
     */
    handleTemplateRefresh(): void {

        // The DOM node of the first rendered field, captured BEFORE the answer is applied. A rebuild
        // recreates it; the in-place path does not. That is the only witness available to a host, and
        // it is the one that matters — a value check would read the same either way.
        const before: Element = this.firstFieldNode('.xx-af-mock-form');

        this.afterChangeDetection(() => {
            const after: Element = this.firstFieldNode('.xx-af-mock-form');
            this.templateRefreshMode = before && after === before ? 'in place' : 'Neuaufbau';
            this.log('Template-Refresh', `Server-Definition ${this.templateRefreshMode} übernommen`,
                this.templateRefreshMode === 'in place' ? 'success' : 'warn');
        });
    }

    /**
     * The first rendered field element of ONE embedded form, or null before it has rendered.
     *
     * <p>@ticket TM-1000002003: scoped, because the page mounts two independent forms — an unscoped
     * query would always answer with the first one and report the mock's verdict for the backend
     * form.</p>
     *
     * @param scope CSS selector of the section the form is mounted in
     */
    private firstFieldNode(scope: string): Element {
        const host: HTMLElement = this.elementRef?.nativeElement;
        const section: Element = host ? host.querySelector(scope) : null;
        return section ? section.querySelector('mvs-form-field') : null;
    }

    handleFormChange(value: DtoDetail): void {

        this.afterChangeDetection(() => {

            const wasVisible: boolean = this.detailsVisible;

            this.formValue = value;

            const isVisible: boolean = this.detailsVisible;

            if (wasVisible !== isVisible) {
                this.log(
                    'Sichtbarkeit',
                    isVisible ? '„Details“ eingeblendet' : '„Details“ ausgeblendet',
                    'warn');
            }
        });
    }

    handleFormDirty(dirty: boolean): void {
        this.afterChangeDetection(() => this.dirtyOutput = dirty);
    }

    handleFormStateChange(state: MvsFormStateDto): void {
        this.afterChangeDetection(() => this.state = state);
    }

    handleChangedObject(event: ObjectChangeInformation): void {

        this.afterChangeDetection(() => {

            this.saveCount++;

            const version = event?.after?.['version'] ?? '?';
            const stamp = event?.after?.['gen_serverStamp'] ?? '?';

            const action: string =
                event?.action === ObjectChangeInformationActionEnum.created ? 'Angelegt' : 'Gespeichert';

            // @ticket TM-1000002003: „noch ungespeichert" is read off the form's own state rather than
            // from a dedicated merge event — a control the user typed into during the flight is still
            // dirty after the merge, which is exactly what `state.dirty` reports one tick later.
            const pending: string = this.state && this.state.dirty
                ? ' — Eingaben aus der Laufzeit behalten, werden nachgespeichert'
                : '';

            this.log('onChangedObject', `${action} — Version ${version}, Server-Zeitstempel ${stamp}${pending}`, 'success');
        });
    }

    handleObjectChangeError(error: any): void {
        this.afterChangeDetection(() =>
            this.log('onObjectChangeError', error?.message ?? 'Unbekannter Fehler beim Speichern.', 'danger'));
    }

    // ----------------------------------------------------------------------------------- //
    // PAGE CONTROLS
    // ----------------------------------------------------------------------------------- //

    handleInPlaceToggle(): void {

        this.log('Seite', `In-Place-Update ${this.inPlace ? 'AN' : 'AUS'}`, 'info');

        // The flag is read once per change by the form; rebuilding makes the A/B unambiguous, because
        // a form that was already mid-save under the other setting cannot muddy the next observation.
        this.handleRebuild();
    }

    handleLatencyChange(): void {
        XxAutosaveFocusMockService.latencyMs = this.latencyMs;
        this.log('Seite', `Antwortzeit des Mocks: ${this.latencyMs} ms`, 'info');
    }

    /** Rebuild the embedded component from scratch and drop every collected state. */
    handleRebuild(): void {

        this.formInstance++;

        this.formValue = null;
        this.state = null;
        this.dirtyOutput = null;
        this.saveCount = 0;
        this.templateRefreshMode = '—';
        this.cachedOverwrite = null;
        this.cachedSignature = null;

        this.log('Seite', 'Formular neu aufgebaut', 'info');
    }

    handleClearLog(): void {
        this.eventLog = [];
    }

    // ----------------------------------------------------------------------------------- //
    // ECHTES BACKEND-FORMULAR — dieselbe Frage, ohne Mock
    // ----------------------------------------------------------------------------------- //

    /**
     * @ticket TM-1000002003
     * The second section mounts the SAME component against a real entity of the running backend.
     *
     * <h3>Why it exists next to the mock</h3>
     * The mock answers instantly, with a form definition this page wrote itself. Everything above is
     * therefore only evidence about the framework, not about a form anybody actually uses. A real
     * {@code cr.Customer} brings what a mock cannot: the real number of fields, real value lists, real
     * validators, real latency and a real save. If the caret survives there, the claim holds.
     *
     * <h3>The one deliberate difference</h3>
     * Autosave is OFF by default here and the action bar is left visible. The mock row is scratch data;
     * a customer is not. Writing happens when someone presses „Speichern“ or explicitly switches
     * autosave on — the page never starts writing to a real row on its own.
     *
     * <p>Every backend call goes through the embedded component. This page contains no HTTP client and
     * no service call of its own.</p>
     */

    /** Entity type of the real form — free text, so any entity can be tried. */
    realObjectType: string = 'cr.Customer';

    /** Row id of the real form. */
    realObjectId: number = 18561;

    /** THE toggle for the real form, independent of the mock above. */
    realInPlace: boolean = true;

    /** Autosave for the real form — OFF by default, see the TSDoc above. */
    realAutoSave: boolean = false;

    /** False until „Formular laden“ was pressed: nothing is requested from the backend before that. */
    realMounted: boolean = false;

    /** Track key of the real form's single-element `@for` — bumped to force a full remount. */
    realInstance: number = 0;

    /** „in place“ / „Neuaufbau“ for the real form, derived from DOM node identity. */
    realTemplateRefreshMode: string = '—';

    /** Latest `(onFormStateChange)` snapshot of the real form. */
    realState: MvsFormStateDto = null;

    /** The state triple already reported, so the log carries changes and not every pass. */
    private realStateSignature: string = null;

    /** How many saves of the real row came back. */
    realSaveCount: number = 0;

    /** German one-liner shown instead of the form when it cannot be mounted or does not answer. */
    realNotice: string = null;

    /** True between „Formular laden“ and the first published state. */
    private realAwaitingAnswer: boolean = false;

    /** The „no answer“ timer, so a new load and ngOnDestroy can take it back. */
    private realAnswerTimer: any = null;

    /** How long the section waits for the first state before it says so. */
    private static readonly REAL_ANSWER_TIMEOUT_MS: number = 8000;

    /**
     * Load, or reload, the real form.
     *
     * <p>@ticket TM-1000002003: the object type is FREE TEXT, so it can name something that is not
     * registered — and the embedded component resolves its CRUD service without checking, then calls
     * `get` on it. A typo would therefore throw inside the component's own init, which is the one
     * thing a page offering a free-text field must not let happen. Asking first costs one map lookup
     * and turns the whole class of typos into a sentence. Nothing is mounted until the answer is
     * yes.</p>
     */
    handleRealLoad(): void {

        this.clearRealAnswerTimer();
        this.realNotice = null;

        if (!this.realObjectType || !this.realObjectId) {
            this.realMounted = false;
            this.realNotice = 'Objekttyp und Objekt-ID werden benötigt.';
            this.log('Backend', this.realNotice, 'warn');
            return;
        }

        // getCrudService answers null for an unregistered type — it does not throw, and neither does
        // this page. The component behind it would.
        if (!this.coreService.getCrudService(this.realObjectType)) {
            this.realMounted = false;
            this.realNotice = `Für „${this.realObjectType}“ ist kein CRUD-Service registriert. `
                + 'Schreibweise prüfen, zum Beispiel „cr.Customer“.';
            this.log('Backend', this.realNotice, 'danger');
            return;
        }

        this.realInstance++;
        this.realMounted = true;
        this.realState = null;
        this.realStateSignature = null;
        this.realTemplateRefreshMode = '—';
        this.realSaveCount = 0;
        this.realAwaitingAnswer = true;

        this.log('Backend', `${this.realObjectType} #${this.realObjectId} wird geladen `
            + `(In-Place-Update ${this.realInPlace ? 'AN' : 'AUS'}, `
            + `Autosave ${this.realAutoSave ? 'AN' : 'AUS'}).`, 'info');

        this.startRealAnswerTimer();
    }

    /**
     * @ticket TM-1000002003
     * Say something when the backend never answers.
     *
     * <p>A failed LOAD has no output on the embedded component — `onObjectChangeError` reports writes,
     * not reads — so a page cannot be told that the GET failed. What it CAN observe is that no state
     * was ever published. With no backend reachable that is exactly what happens, and without this the
     * section would sit empty and look like it was still working.</p>
     *
     * <p>Bound outside the Angular zone: a timer that never fires must not wake change detection, and
     * the one that does re-enters explicitly.</p>
     */
    private startRealAnswerTimer(): void {

        this.zone.runOutsideAngular(() => {
            this.realAnswerTimer = setTimeout(() => {

                if (!this.realAwaitingAnswer) {
                    return;
                }

                this.zone.run(() => {
                    this.realNotice = 'Keine Antwort vom Backend. Läuft es, und ist der Datensatz '
                        + 'vorhanden? Die Einzelheiten stehen in der Browser-Konsole — ein '
                        + 'fehlgeschlagenes Laden meldet das eingebettete Formular nicht nach außen.';
                    this.log('Backend', this.realNotice, 'danger');
                });

            }, XxAutosaveFocusTestPage.REAL_ANSWER_TIMEOUT_MS);
        });
    }

    private clearRealAnswerTimer(): void {
        if (this.realAnswerTimer) {
            clearTimeout(this.realAnswerTimer);
            this.realAnswerTimer = null;
        }
        this.realAwaitingAnswer = false;
    }

    /** The flag is read once per mount, so switching it reloads — same discipline as the mock above. */
    handleRealInPlaceToggle(): void {

        this.log('Backend', `In-Place-Update ${this.realInPlace ? 'AN' : 'AUS'}`, 'info');

        if (this.realMounted) {
            this.handleRealLoad();
        }
    }

    /** Autosave is an input of the created component, so it takes effect on the next mount. */
    handleRealAutoSaveToggle(): void {

        this.log('Backend', `Autosave ${this.realAutoSave ? 'AN — es wird wirklich geschrieben' : 'AUS'}`,
            this.realAutoSave ? 'warn' : 'info');

        if (this.realMounted) {
            this.handleRealLoad();
        }
    }

    /**
     * @ticket TM-1000002003
     * The real form asked the backend for a new definition. Same witness as for the mock: the DOM node
     * of the first rendered field, compared across the answer.
     */
    handleRealTemplateRefresh(): void {

        const before: Element = this.firstFieldNode('.xx-af-real-form');

        this.log('onTemplateRefresh', 'Server-Definition erhalten', 'info');

        this.afterChangeDetection(() => {
            const after: Element = this.firstFieldNode('.xx-af-real-form');
            this.realTemplateRefreshMode = before && after === before ? 'in place' : 'Neuaufbau';
            this.log('Template-Refresh', `Server-Definition ${this.realTemplateRefreshMode} übernommen`,
                this.realTemplateRefreshMode === 'in place' ? 'success' : 'warn');
        });
    }

    handleRealFormStateChange(state: MvsFormStateDto): void {

        this.afterChangeDetection(() => {

            this.realState = state;

            // the first published state is the proof that the round trip came back at all
            if (this.realAwaitingAnswer) {
                this.clearRealAnswerTimer();
                this.realNotice = null;
            }

            // Only a CHANGED triple is worth a line — otherwise the log fills with the same row and
            // the events that matter scroll out of sight.
            const signature: string = `${state?.dirty}|${state?.valid}|${state?.busy}`;

            if (signature === this.realStateSignature) {
                return;
            }

            this.realStateSignature = signature;

            this.log('onFormStateChange',
                `geändert: ${state?.dirty} · gültig: ${state?.valid} · speichert: ${state?.busy}`, 'info');
        });
    }

    handleRealChangedObject(event: ObjectChangeInformation): void {

        this.afterChangeDetection(() => {

            this.realSaveCount++;

            const action: string =
                event?.action === ObjectChangeInformationActionEnum.created ? 'Angelegt' : 'Gespeichert';

            const pending: string = this.realState && this.realState.dirty
                ? ' — Eingaben aus der Laufzeit behalten'
                : '';

            this.log('onChangedObject', `${action} — ${this.realObjectType} #${this.realObjectId}${pending}`,
                'success');
        });
    }

    handleRealObjectChangeError(error: any): void {
        this.afterChangeDetection(() =>
            this.log('onObjectChangeError',
                `Backend: ${error?.message ?? 'Unbekannter Fehler beim Speichern.'}`, 'danger'));
    }

    // ----------------------------------------------------------------------------------- //
    // FOCUS DIAGNOSTICS — reading only
    // ----------------------------------------------------------------------------------- //

    /**
     * @ticket TM-1000002003
     * Focus LEFT an element inside this page. When it landed on the body, the element it came from was
     * destroyed under the user — which is the defect, caught at the moment it happens.
     *
     * <p>`relatedTarget` is the element focus is moving TO; it is null when focus goes nowhere, which
     * is exactly what a destroyed input produces.</p>
     */
    @HostListener('focusout', ['$event'])
    handleFocusOut(event: FocusEvent): void {

        const next: any = event.relatedTarget;

        if (next) {
            return; // an ordinary move between two fields
        }

        const label: string = this.labelOf(event.target as HTMLElement) ?? 'unbekanntes Feld';

        this.log('Fokus', `Fokus verloren: aktiv war „${label}“, jetzt Body`, 'danger');
    }

    @HostListener('focusin', ['$event'])
    handleFocusIn(event: FocusEvent): void {
        this.refreshFocusDiagnostics();
    }

    /**
     * Read where the caret is. Never moves it.
     *
     * <p>@ticket TM-1000002003: called from OUTSIDE the Angular zone, so it computes first and
     * re-enters the zone only when something actually moved. A cursor that sits still therefore
     * triggers no render at all, which is the whole difference to the poll this replaced.</p>
     */
    private refreshFocusDiagnostics(): void {

        const active = document.activeElement as HTMLInputElement | HTMLTextAreaElement | null;

        let label: string = '— (Body)';
        let start: number = null;
        let end: number = null;
        let value: string = null;

        if (active && this.elementRef?.nativeElement?.contains(active)
            && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {

            label = this.labelOf(active) ?? '— (ohne Beschriftung)';
            value = active.value;

            // A number input throws on selectionStart in some browsers — a diagnostics panel must
            // never be the thing that breaks the page it is diagnosing.
            try {
                start = active.selectionStart;
                end = active.selectionEnd;
            } catch {
                start = null;
                end = null;
            }
        }

        if (label === this.focusedFieldLabel && start === this.caretStart
            && end === this.caretEnd && value === this.focusedFieldValue) {
            return; // nothing moved — do not wake change detection
        }

        this.zone.run(() => {
            this.focusedFieldLabel = label;
            this.caretStart = start;
            this.caretEnd = end;
            this.focusedFieldValue = value;
        });
    }

    /**
     * The German label of the field an input belongs to, read from the `<label [for]>` the field
     * wrapper renders. The element ids PrimeNG generates are regenerated on every rebuild and say
     * nothing a human would recognise.
     */
    private labelOf(element: HTMLElement): string | null {

        if (!element) {
            return null;
        }

        const container = element.closest('.field') || element.closest('[class*="field"]');
        const label = container?.querySelector('label');

        const text: string = label?.textContent?.trim();

        return text ? text.replace(/\s*\*$/, '') : null;
    }

    // ----------------------------------------------------------------------------------- //

    private log(name: string, detail: string, severity: XxAutosaveFocusLogEntry['severity']): void {

        const now = new Date();
        const time = `${this.pad(now.getHours(), 2)}:${this.pad(now.getMinutes(), 2)}:`
            + `${this.pad(now.getSeconds(), 2)}.${this.pad(now.getMilliseconds(), 3)}`;

        this.eventLog = [
            {seq: ++this.seq, time: time, name: name, detail: detail, severity: severity},
            // keep the list short enough to stay readable while typing
            ...this.eventLog
        ].slice(0, 80);
    }

    private pad(value: number, length: number): string {
        return String(value).padStart(length, '0');
    }
}
